import {
  fontSizes,
  lineHeights,
  fontWeights,
  letterSpacings,
  fontFamilies,
} from "../../primitives/typography";
import type { TypographyTheme, FontStyles } from "../../semantic/typography";
import { NEUTRAL } from "../../primitives/colors";

const createStyles = (
  role: keyof typeof fontSizes,
  size: "large" | "medium" | "small",
  weight: keyof typeof fontWeights,
  letterSpacing: number = letterSpacings.normal,
  lineHeightOverride?: number,
): FontStyles => ({
  color: NEUTRAL[10],
  fontFamily: fontFamilies.default,
  fontSize: fontSizes[role][size],
  lineHeight: lineHeightOverride ?? lineHeights[role][size],
  fontWeight: fontWeights[weight],
  letterSpacing,
});

export const baseTypography: TypographyTheme = {
  display: {
    large: {
      regular: createStyles("display", "large", "regular"),
      emphasized: createStyles("display", "large", "semiBold"),
      bold: createStyles("display", "large", "bold"),
    },
    medium: {
      regular: createStyles("display", "medium", "regular"),
      emphasized: createStyles("display", "medium", "semiBold"),
      bold: createStyles("display", "medium", "bold"),
    },
    small: {
      regular: createStyles("display", "small", "regular"),
      emphasized: createStyles("display", "small", "semiBold"),
      bold: createStyles("display", "small", "bold"),
    },
  },
  headline: {
    large: {
      regular: createStyles("headline", "large", "regular"),
      emphasized: createStyles("headline", "large", "semiBold"),
      bold: createStyles("headline", "large", "bold"),
    },
    medium: {
      regular: createStyles("headline", "medium", "regular"),
      emphasized: createStyles("headline", "medium", "semiBold"),
      bold: createStyles("headline", "medium", "bold"),
    },
    small: {
      regular: createStyles("headline", "small", "regular"),
      emphasized: createStyles("headline", "small", "semiBold"),
      bold: createStyles("headline", "small", "bold"),
    },
  },
  title: {
    large: {
      regular: createStyles("title", "large", "regular"),
      emphasized: createStyles("title", "large", "semiBold"),
      bold: createStyles("title", "large", "bold"),
    },
    medium: {
      regular: createStyles("title", "medium", "medium", letterSpacings.wider),
      emphasized: createStyles(
        "title",
        "medium",
        "semiBold",
        letterSpacings.wider,
      ),
      bold: createStyles("title", "medium", "bold", letterSpacings.wider),
    },
    small: {
      regular: createStyles("title", "small", "medium", letterSpacings.wide),
      emphasized: createStyles(
        "title",
        "small",
        "semiBold",
        letterSpacings.wide,
      ),
      bold: createStyles("title", "small", "bold", letterSpacings.wide),
    },
  },
  label: {
    large: {
      regular: createStyles("label", "large", "medium", letterSpacings.wide),
      emphasized: createStyles(
        "label",
        "large",
        "semiBold",
        letterSpacings.wide,
      ),
      bold: createStyles("label", "large", "bold", letterSpacings.wide),
    },
    medium: {
      regular: createStyles(
        "label",
        "medium",
        "medium",
        letterSpacings.extraWide,
      ),
      emphasized: createStyles(
        "label",
        "medium",
        "semiBold",
        letterSpacings.extraWide,
      ),
      bold: createStyles("label", "medium", "bold", letterSpacings.extraWide),
    },
    small: {
      regular: createStyles(
        "label",
        "small",
        "medium",
        letterSpacings.extraWide,
      ),
      emphasized: createStyles(
        "label",
        "small",
        "semiBold",
        letterSpacings.extraWide,
      ),
      bold: createStyles("label", "small", "bold", letterSpacings.extraWide),
    },
  },
  body: {
    large: {
      regular: createStyles(
        "body",
        "large",
        "regular",
        letterSpacings.extraWide,
      ),
      emphasized: createStyles(
        "body",
        "large",
        "medium",
        letterSpacings.extraWide,
      ),
      bold: createStyles("body", "large", "bold", letterSpacings.extraWide),
    },
    medium: {
      regular: createStyles("body", "medium", "regular", letterSpacings.widest),
      emphasized: createStyles(
        "body",
        "medium",
        "medium",
        letterSpacings.widest,
      ),
      bold: createStyles("body", "medium", "bold", letterSpacings.widest),
    },
    small: {
      regular: createStyles("body", "small", "regular", letterSpacings.relaxed),
      emphasized: createStyles(
        "body",
        "small",
        "medium",
        letterSpacings.relaxed,
      ),
      bold: createStyles("body", "small", "bold", letterSpacings.relaxed),
    },
  },
};
